import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import moment from 'moment';
import { getFirestore, collection, query, where, getDocs, orderBy } from 'firebase/firestore';
import { updateOrdersReport } from '@src/app/actions/userActions';
import {
  Report,
  AnalyticsStats,
  TrendData,
  GstTrendData,
  TrendRow,
  GstTrendRow,
} from './types';

/** Parse createdAt (number or formatted string) into a moment instance. */
const getReportMoment = (report: any) => {
  const created = report.createdAt;
  if (typeof created === 'number') return moment(created);
  if (typeof created === 'string') {
    const m = moment(created, 'D-MMM-YYYY, h:mm:ss a', true);
    return m.isValid() ? m : moment(created);
  }
  return moment.invalid();
};

/** Firestore fetch for a single service-type + GST combination. */
const fetchReportData = async (
  role: string,
  filterId: string,
  startDate: number,
  endDate: number,
  isGstBill: boolean,
  reportType: string,
): Promise<any[]> => {
  const db = getFirestore();
  const reportCollectionName = reportType === 'food' ? 'orders' : 'room-bills';

  const data: any[] = [];

  if (filterId && startDate && endDate) {
    const q = query(
      collection(db, reportCollectionName),
      where(role === 'admin' ? 'restaurantId' : 'userId', '==', filterId),
      where('createdAt', '>=', startDate),
      where('createdAt', '<=', endDate),
      where('isGstBill', '==', !!isGstBill),
      orderBy('createdAt', 'desc'),
    );

    const querySnapshot = await getDocs(q);

    querySnapshot.forEach((doc: any) => {
      const formattedDate = moment(doc?.data()?.createdAt || '').format('D-MMM-YYYY, h:mm:ss a');

      const getFullAddress = (address: any) =>
        address?.type
          ? `${address.type}: ${address.street}, ${address.city}, ${address.state}`
          : '';

      if (reportType === 'food') {
        data.push({
          ...doc?.data(),
          orderId: doc.id,
          user: null,
          ...doc?.data()?.user,
          orderStatus: doc?.data()?.orderStatus?.value,
          ...doc?.data()?.restaurant,
          tabs: null,
          selectedAddress: getFullAddress(doc?.data()?.selectedAddress),
          createdAt: formattedDate,
        });
      } else {
        data.push({
          ...doc?.data(),
          orderId: doc.id,
          createdAt: formattedDate,
        });
      }
    });
  }

  return data;
};

// ---------------------------------------------------------------------------
// Statistics calculator
// ---------------------------------------------------------------------------

const calculateStats = (
  reports: Report[],
  serviceType: string,
  gstFilter: string,
): AnalyticsStats => {
  const empty: AnalyticsStats = {
    totalSales: 0,
    totalOrders: 0,
    totalItems: 0,
    averageOrderValue: 0,
    mostOrderedItems: [],
    categoryData: [],
    paymentModeData: [],
    salesOverTime: [],
  };

  if (!reports || reports.length === 0) return empty;

  let totalSales = 0;
  let totalItems = 0;

  const itemFrequencies: Record<string, number> = {};
  const categorySales: Record<string, number> = {};
  const categoryItems: Record<string, Record<string, number>> = {};
  const paymentModeSales: Record<string, number> = {};
  const salesByDate: Record<string, number> = {};

  const filteredReports = reports.filter((report) => {
    const isRoom = report.rooms && report.rooms.length > 0;
    if (serviceType === 'room' && !isRoom) return false;
    if (serviceType === 'food' && isRoom) return false;
    if (gstFilter === 'gst' && !report.isGstBill) return false;
    if (gstFilter === 'test-bills' && report.isGstBill) return false;
    return true;
  });

  filteredReports.forEach((report) => {
    try {
      const isRoom = report.rooms && report.rooms.length > 0;
      const m = getReportMoment(report);
      const dateKey = m.isValid() ? m.format('YYYY-MM-DD') : 'Unknown';

      if (isRoom) {
        const orderTotal = Number(report.totalCost ?? 0);
        totalSales += orderTotal;
        totalItems += report.rooms?.length ?? 0;
        salesByDate[dateKey] = (salesByDate[dateKey] ?? 0) + orderTotal;

        report.rooms?.forEach((room) => {
          const roomType = room?.roomType ?? 'Unknown';
          const roomCost = Number(room?.roomCost ?? 0);
          itemFrequencies[roomType] = (itemFrequencies[roomType] ?? 0) + 1;
          categorySales['Room Service'] = (categorySales['Room Service'] ?? 0) + roomCost;
          if (!categoryItems['Room Service']) categoryItems['Room Service'] = {};
          categoryItems['Room Service'][roomType] =
            (categoryItems['Room Service'][roomType] ?? 0) + roomCost;
        });

        const paymentMode = report.selectedPayment ?? 'Cash';
        paymentModeSales[paymentMode] = (paymentModeSales[paymentMode] ?? 0) + orderTotal;
      } else {
        if (report.cart) {
          Object.entries(report.cart).forEach(([, item]) => {
            const itemValue = Number(item?.selectedVariants?.value ?? 0);
            const itemQuantity = Number(item?.selected ?? 0);
            const itemTotal = itemValue * itemQuantity;
            totalSales += itemTotal;
            totalItems += itemQuantity;
            salesByDate[dateKey] = (salesByDate[dateKey] ?? 0) + itemTotal;

            if (item?.name) {
              itemFrequencies[item.name] = (itemFrequencies[item.name] ?? 0) + itemQuantity;
            }
            if (item?.category) {
              categorySales[item.category] = (categorySales[item.category] ?? 0) + itemTotal;
              if (!categoryItems[item.category]) categoryItems[item.category] = {};
              const itemName = item?.name ?? 'Unknown';
              categoryItems[item.category][itemName] =
                (categoryItems[item.category][itemName] ?? 0) + itemTotal;
            }
          });
        }

        const paymentMode = report.selectedPayment ?? 'Cash';
        const orderTotal = Number(report.totalCost ?? 0);
        paymentModeSales[paymentMode] = (paymentModeSales[paymentMode] ?? 0) + orderTotal;
      }
    } catch (err) {
      console.error('Error processing report:', err);
    }
  });

  const totalOrders = filteredReports.length;

  const totalItemOrders = Object.values(itemFrequencies).reduce((sum, v) => sum + v, 0);
  const mostOrderedItems = Object.entries(itemFrequencies)
    .map(([name, orders]) => ({
      name,
      orders,
      percentage: totalItemOrders > 0 ? Number(((orders / totalItemOrders) * 100).toFixed(1)) : 0,
    }))
    .sort((a, b) => b.orders - a.orders)
    .slice(0, 20);

  const totalCategorySales = Object.values(categorySales).reduce((sum, v) => sum + v, 0);
  const categoryData = Object.entries(categorySales)
    .map(([name, value]) => ({
      name,
      value,
      percentage: totalCategorySales > 0 ? Number(((value / totalCategorySales) * 100).toFixed(1)) : 0,
      items: Object.entries(categoryItems[name] ?? {})
        .map(([itemName, itemValue]) => ({ name: itemName, value: itemValue }))
        .sort((a, b) => b.value - a.value),
    }))
    .sort((a, b) => b.value - a.value);

  const paymentModeData = Object.entries(paymentModeSales)
    .map(([name, value]) => ({ name, value }))
    .sort((a, b) => b.value - a.value);

  const salesOverTime = Object.entries(salesByDate)
    .map(([date, value]) => ({
      date: moment(date, 'YYYY-MM-DD').format('D MMM'),
      sales: value,
    }))
    .sort((a, b) => {
      const ma = moment(a.date, 'D MMM');
      const mb = moment(b.date, 'D MMM');
      if (!ma.isValid() || !mb.isValid()) return 0;
      return ma.valueOf() - mb.valueOf();
    });

  const averageOrderValue = totalOrders > 0 ? totalSales / totalOrders : 0;

  return {
    totalSales,
    totalOrders,
    totalItems,
    averageOrderValue,
    mostOrderedItems,
    categoryData,
    paymentModeData,
    salesOverTime,
  };
};

// ---------------------------------------------------------------------------
// Trend builders
// ---------------------------------------------------------------------------

const buildTrendData = (
  reports: Report[],
  serviceType: string,
  gstFilter: string,
  startDate: Date,
  endDate: Date,
): TrendData => {
  const filteredReports = reports.filter((report) => {
    const isRoom = report.rooms && (report.rooms as any[])?.length > 0;
    if (serviceType === 'room' && !isRoom) return false;
    if (serviceType === 'food' && isRoom) return false;
    if (gstFilter === 'gst' && !report.isGstBill) return false;
    if (gstFilter === 'test-bills' && report.isGstBill) return false;
    return true;
  });

  const start = moment(startDate);
  const end = moment(endDate);
  const useQuarters = start.year() !== end.year();

  const totalsByPeriod: Record<string, { food: number; room: number }> = {};

  filteredReports.forEach((report) => {
    const m = getReportMoment(report);
    if (!m.isValid()) return;
    const isRoom = Array.isArray(report.rooms) && report.rooms.length > 0;
    const amount = Number(report.totalCost ?? 0);
    const periodKey = useQuarters ? `${m.year()}-Q${m.quarter()}` : m.format('YYYY-MM');
    if (!totalsByPeriod[periodKey]) totalsByPeriod[periodKey] = { food: 0, room: 0 };
    if (isRoom) totalsByPeriod[periodKey].room += amount;
    else totalsByPeriod[periodKey].food += amount;
  });

  const sortedKeys = Object.keys(totalsByPeriod).sort((a, b) => {
    if (useQuarters) {
      const [ya, qa] = a.split('-Q').map(Number);
      const [yb, qb] = b.split('-Q').map(Number);
      return ya !== yb ? ya - yb : qa - qb;
    }
    return a.localeCompare(b);
  });

  const rows: TrendRow[] = [];
  let prevFood = 0;
  let prevRoom = 0;
  sortedKeys.forEach((key, idx) => {
    const { food, room } = totalsByPeriod[key];
    const foodGrowth =
      idx === 0 || prevFood === 0 ? null : Number((((food - prevFood) / prevFood) * 100).toFixed(1));
    const roomGrowth =
      idx === 0 || prevRoom === 0 ? null : Number((((room - prevRoom) / prevRoom) * 100).toFixed(1));
    rows.push({ period: key, food, room, foodGrowthPct: foodGrowth, roomGrowthPct: roomGrowth });
    prevFood = food;
    prevRoom = room;
  });

  return { rows, useQuarters };
};

const buildGstTrendData = (
  reports: Report[],
  serviceType: string,
  gstFilter: string,
  startDate: Date,
  endDate: Date,
): GstTrendData => {
  const filteredReports = reports.filter((report) => {
    const isRoom = report.rooms && (report.rooms as any[])?.length > 0;
    if (serviceType === 'room' && !isRoom) return false;
    if (serviceType === 'food' && isRoom) return false;
    if (gstFilter === 'gst' && !report.isGstBill) return false;
    if (gstFilter === 'test-bills' && report.isGstBill) return false;
    return true;
  });

  const start = moment(startDate);
  const end = moment(endDate);
  const useQuarters = start.year() !== end.year();

  const gstByPeriod: Record<string, { foodGST: number; roomGST: number }> = {};

  filteredReports.forEach((report) => {
    const m = getReportMoment(report);
    if (!m.isValid()) return;
    const isRoom = Array.isArray(report.rooms) && report.rooms.length > 0;
    const gst = Number(report.gstBill ?? 0);
    const periodKey = useQuarters ? `${m.year()}-Q${m.quarter()}` : m.format('YYYY-MM');
    if (!gstByPeriod[periodKey]) gstByPeriod[periodKey] = { foodGST: 0, roomGST: 0 };
    if (isRoom) gstByPeriod[periodKey].roomGST += gst;
    else gstByPeriod[periodKey].foodGST += gst;
  });

  const sortedKeys = Object.keys(gstByPeriod).sort((a, b) => {
    if (useQuarters) {
      const [ya, qa] = a.split('-Q').map(Number);
      const [yb, qb] = b.split('-Q').map(Number);
      return ya !== yb ? ya - yb : qa - qb;
    }
    return a.localeCompare(b);
  });

  const rows: GstTrendRow[] = [];
  let prevFood = 0;
  let prevRoom = 0;
  sortedKeys.forEach((key, idx) => {
    const { foodGST, roomGST } = gstByPeriod[key];
    const foodGrowth =
      idx === 0 || prevFood === 0
        ? null
        : Number((((foodGST - prevFood) / prevFood) * 100).toFixed(1));
    const roomGrowth =
      idx === 0 || prevRoom === 0
        ? null
        : Number((((roomGST - prevRoom) / prevRoom) * 100).toFixed(1));
    rows.push({
      period: key,
      foodGST,
      roomGST,
      foodGSTGrowthPct: foodGrowth,
      roomGSTGrowthPct: roomGrowth,
    });
    prevFood = foodGST;
    prevRoom = roomGST;
  });

  return { rows, useQuarters };
};

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export const useAnalyticsData = (reports: Report[], isSuperAdmin = false) => {
  const dispatch = useDispatch();
  const params = useParams();

  const [startDate, setStartDate] = useState(new Date());
  const [endDate, setEndDate] = useState(new Date());
  const [loading, setLoading] = useState(true);
  const [serviceType, setServiceType] = useState<string>('all');
  const [gstFilter, setGstFilter] = useState<string>('gst');
  const effectiveGstFilter = isSuperAdmin ? gstFilter : 'all';
  const [error, setError] = useState<string | null>(null);

  // Default date range: last 7 days
  useEffect(() => {
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - 7);
    setStartDate(start);
    setEndDate(end);
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      // Use start-of-day for start date and end-of-day for end date so the selected
      // range is fully inclusive (e.g. "today" includes all of today's data).
      const startDateIn = moment(startDate).startOf('day').valueOf();
      const endDateIn = moment(endDate).endOf('day').valueOf();

      if (!params?.restaurantId) {
        throw new Error('Restaurant ID is required');
      }

      const serviceTypes = serviceType === 'all' ? ['room', 'food'] : [serviceType];
      const gstFilters = effectiveGstFilter === 'all' ? [true, false] : [effectiveGstFilter === 'gst'];

      const allPromises: Promise<any[]>[] = [];
      for (const st of serviceTypes) {
        for (const gst of gstFilters) {
          allPromises.push(
            fetchReportData('admin', params.restaurantId, startDateIn, endDateIn, gst, st),
          );
        }
      }

      const allResults = await Promise.all(allPromises);
      const combinedData = allResults.flat();

      const uniqueData = combinedData.filter(
        (item, index, self) => index === self.findIndex((t) => t.orderId === item.orderId),
      );

      dispatch(updateOrdersReport(uniqueData.length > 0 ? uniqueData : []));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch data');
      console.error('Error fetching data:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- fetchData identity stable
  }, [startDate, endDate, serviceType, effectiveGstFilter]);

  const stats = calculateStats(reports, serviceType, effectiveGstFilter);
  const trendData = buildTrendData(reports, serviceType, effectiveGstFilter, startDate, endDate);
  const gstTrendData = buildGstTrendData(reports, serviceType, effectiveGstFilter, startDate, endDate);

  return {
    // Filter state
    startDate,
    endDate,
    serviceType,
    gstFilter,
    setStartDate,
    setEndDate,
    setServiceType,
    setGstFilter,
    // Loading / error
    loading,
    error,
    retry: fetchData,
    // Computed data
    stats,
    trendData,
    gstTrendData,
  };
};
